import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import GenericInput from "src/components/input-components/GenericTextInput";
import ImagePicker from "src/components/input-components/GenericImagePicker";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import GenericTextAreaInput from "src/components/input-components/GenericTextAreaInput";

// --- Type Definitions ---
export interface IProjectsV1Widget {
  type: string; // should be WidgetType.ProjectsV1 in parent
  Title: string;
  Description: string;
  Subtitle: string;
  LinkTitle: string;
  Link: string;
  Images: string[]; // Exactly 7 images required.
}

// --- Props for the ProjectsV1Item component ---
interface ProjectsV1ItemProps {
  item: IProjectsV1Widget;
  error: any;
  onChange: (updatedItem: IProjectsV1Widget, field: string) => void;
  onblur: (field: string, index?: number) => void;
}

const ProjectsV1Item: React.FC<ProjectsV1ItemProps> = ({
  item,
  error,
  onChange,
  onblur,
}) => {
  // Extract overall fields.
  const widgetTitle: string =
    typeof item.Title === "string" ? item.Title : "";
  const widgetDescription: string =
    typeof item.Description === "string" ? item.Description : "";
  const widgetSubtitle: string =
    typeof item.Subtitle === "string" ? item.Subtitle : "";
  const widgetLinkTitle: string =
    typeof item.LinkTitle === "string" ? item.LinkTitle : "";
  const widgetLink: string =
    typeof item.Link === "string" ? item.Link : "";

  // Ensure Images is an array of exactly 7 strings.
  const images: string[] = (() => {
    let imgs = Array.isArray(item.Images) ? [...item.Images] : [];
    // Pad with empty strings if less than 7
    while (imgs.length < 7) {
      imgs.push("");
    }
    // Slice if more than 7
    if (imgs.length > 7) {
      imgs = imgs.slice(0, 7);
    }
    return imgs;
  })();

  // State for controlling the ImagePicker.
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<number | null>(
    null
  );

  // Helper: extract value from event or raw.
  const getValue = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ): string => (typeof e === "string" ? e : e.target.value);

  // Handlers for overall widget fields.
  const handleTitleChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange({ ...item, Title: getValue(e) }, "Title");
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange({ ...item, Description: getValue(e) }, "Description");
  };

  const handleSubtitleChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange({ ...item, Subtitle: getValue(e) }, "Subtitle");
  };

  const handleLinkTitleChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange({ ...item, LinkTitle: getValue(e) }, "LinkTitle");
  };

  const handleLinkChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange({ ...item, Link: getValue(e) }, "Link");
  };

  // Handler for updating a specific image.
  const handleImageSelect = (selected: string | string[]) => {
    if (currentImageIndex !== null) {
      const newImage = Array.isArray(selected) ? selected[0] : selected;
      const updatedImages = [...images];
      updatedImages[currentImageIndex] = newImage;
      onChange({ ...item, Images: updatedImages }, "Images");
    }
    setImagePickerOpen(false);
    setCurrentImageIndex(null);
  };

  // Open image picker for a given index.
  const handleOpenImagePicker = (index: number) => {
    setCurrentImageIndex(index);
    setImagePickerOpen(true);
  };

  // Clear image at an index.
  const handleClearImage = (index: number) => {
    const updatedImages = [...images];
    updatedImages[index] = "";
    onChange({ ...item, Images: updatedImages }, "Images");
  };

  return (
    <Card elevation={2} sx={{ p: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Projects Widget
        </Typography>

        {/* Overall widget inputs */}
        <GenericInput
          type="text"
          name="Title"
          title="Widget Title"
          value={widgetTitle}
          error={error?.Title || ""}
          onChange={handleTitleChange}
          onBlur={() => onblur("Title")}
          disabled={false}
        />
        <GenericTextAreaInput
          name="Description"
          title="Widget Description"
          value={widgetDescription}
          error={error?.Description || ""}
          onChange={handleDescriptionChange}
          onBlur={() => onblur("Description")}
          disabled={false}
        />
        <GenericInput
          type="text"
          name="Subtitle"
          title="Widget Subtitle"
          value={widgetSubtitle}
          error={error?.Subtitle || ""}
          onChange={handleSubtitleChange}
          onBlur={() => onblur("Subtitle")}
          disabled={false}
        />
        <GenericInput
          type="text"
          name="LinkTitle"
          title="Widget Link Title"
          value={widgetLinkTitle}
          error={error?.LinkTitle || ""}
          onChange={handleLinkTitleChange}
          onBlur={() => onblur("LinkTitle")}
          disabled={false}
        />
        <GenericInput
          type="text"
          name="Link"
          title="Widget Link"
          value={widgetLink}
          error={error?.Link || ""}
          onChange={handleLinkChange}
          onBlur={() => onblur("Link")}
          disabled={false}
        />

        {/* Images Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Project Images (Exactly 7 required)
        </Typography>
        <Grid container spacing={2}>
          {images.map((img, index) => (
            <Grid item xs={12} sm={4} md={3} key={index}>
              <Box
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  height: 150,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  cursor: "pointer",
                  backgroundColor: "#f9f9f9",
                }}
                onClick={() => handleOpenImagePicker(index)}
              >
                {img ? (
                  <img
                    src={img}
                    alt={`Project ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Select Image
                  </Typography>
                )}
                {img && (
                  <IconButton
                    color="error"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      backgroundColor: "rgba(255,255,255,0.7)",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearImage(index);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
      {imagePickerOpen && (
        <ImagePicker
          open={imagePickerOpen}
          multiSelect={false}
          onClose={() => setImagePickerOpen(false)}
          onSelect={handleImageSelect}
        />
      )}
    </Card>
  );
};

export default ProjectsV1Item;
